import React from "react";
import PropTypes from "prop-types";
import SectionCard from "../../SectionCard";
import ContactDetails from "../../../contact_details/ContactDetails";

/**
 * ConnectSection — the shared profile "Connect" card: how a viewer reaches this entity.
 *
 * A `<SectionCard>` listing the entity's contact details (website, socials, email/phone where the
 * data lane carries them) above an `actions` slot for whatever talk-to-them affordance the engine
 * offers this viewer — typically the message button. Model-agnostic: it takes the plain
 * `[{ type, value }]` rows and an already-gated node, so who may message whom stays an engine
 * decision. Renders muted empty-state copy when there are no details and no actions; deciding
 * whether to show the card at all is the caller's job (as with `<AboutSection>`).
 *
 * @param {Object} props
 * @param {Array} [props.contactDetails] - Contact rows as the profile views return them
 * @param {React.ReactNode} [props.actions] - Engine-gated affordances rendered under the details
 * @param {string} [props.title] - Section title (default "Connect")
 * @param {string} [props.emptyText] - Copy shown when there is nothing to show
 * @param {boolean} [props.editable] - Whether the viewer may edit this section
 * @param {Function} [props.onEdit] - Click handler for the standard edit pen (shown only when editable)
 * @param {string} [props.editButtonId] - Optional DOM id for the edit pen (stable hook for system tests)
 * @param {string} [props.id] - Optional DOM id (e.g. for system tests / deep links)
 */
// Figma's card title: "Connect with Michelle". People go by their first name (a leading title such as
// "Dr." is skipped); companies, products and outlets by their whole name.
export function connectTitle(name) {
  return name ? `Connect with ${name}` : "Connect";
}

export function firstName(fullName) {
  const parts = (fullName || "").trim().split(/\s+/);
  return parts.find((part) => !part.endsWith(".")) || parts[0] || "";
}

function ConnectSection({
  contactDetails = [],
  actions = null,
  title = "Connect",
  emptyText = "No contact details added yet.",
  editable = false,
  onEdit = null,
  editButtonId = undefined,
  id = undefined,
}) {
  const details = contactDetails ?? [];
  const hasContent = details.length > 0 || actions != null;

  return (
    <SectionCard id={id} title={title} editable={editable} onEdit={onEdit} editButtonId={editButtonId}>
      {details.length > 0 && <ContactDetails contactDetails={details} />}
      {actions != null && <div className={details.length > 0 ? "mt-3" : ""}>{actions}</div>}
      {!hasContent && <div className="text-muted">{emptyText}</div>}
    </SectionCard>
  );
}

ConnectSection.propTypes = {
  contactDetails: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      value: PropTypes.string,
    }),
  ),
  actions: PropTypes.node,
  title: PropTypes.string,
  emptyText: PropTypes.string,
  editable: PropTypes.bool,
  onEdit: PropTypes.func,
  editButtonId: PropTypes.string,
  id: PropTypes.string,
};

export default ConnectSection;
